import type {
  AttributeCollectionId,
  AttributeGroupId,
  AttributeId,
  FieldId,
  SelfServiceTaskForm,
} from "../types";

interface FieldIdParts {
  collectionId: AttributeCollectionId;
  collectionIndex: number;
  groupId: AttributeGroupId;
  groupIndex: number;
  attributeId: AttributeId;
}

/**
 * フィールドIDのパーツからフィールドIDを生成する
 */
export const createFieldId = (parts: FieldIdParts): FieldId => {
  return `${parts.collectionId}.${parts.collectionIndex}.${parts.groupId}.${parts.groupIndex}.${parts.attributeId}` as FieldId;
};

/**
 * フィールドIDをパースして各要素に分解する
 * @param fieldId フィールドID (形式: collectionId.collectionIndex.groupId.groupIndex.attributeId)
 * @returns パースされたフィールドID情報、パースに失敗した場合はnull
 */
export const parseFieldId = (fieldId: FieldId): FieldIdParts | null => {
  if (!fieldId || typeof fieldId !== "string") {
    return null;
  }

  try {
    const parts = fieldId.split(".");

    // フィールドIDは5つの部分で構成される必要がある
    if (parts.length !== 5) {
      return null;
    }

    const [collectionId, collectionIndex, groupId, groupIndex, attributeId] =
      parts;

    if (!collectionId || !groupId || !attributeId) {
      return null;
    }

    return {
      collectionId: collectionId as AttributeCollectionId,
      collectionIndex: parseInt(collectionIndex, 10) || 0,
      groupId: groupId as AttributeGroupId,
      groupIndex: parseInt(groupIndex, 10) || 0,
      attributeId: attributeId as AttributeId,
    };
  } catch (_error) {
    return null;
  }
};

/**
 * フィールドIDが有効かどうかを判定する
 */
export const isValidFieldId = (fieldId: FieldId): boolean => {
  return parseFieldId(fieldId) !== null;
};

/**
 * フィールドIDから対応する属性を見つける
 *
 * @param form フォーム定義
 * @param fieldId 検索するフィールドID
 * @returns 見つかった場合は属性と関連情報、見つからない場合はundefined
 */
export function findFieldById(form: SelfServiceTaskForm, fieldId: FieldId) {
  if (!form || !form.form || !form.form.attributeCollections) {
    return undefined;
  }

  try {
    // 動的フィールドIDからパラメータを解析
    const params = parseFieldId(fieldId);
    if (!params) {
      return undefined;
    }

    // コレクションを取得
    const collection = form.form.attributeCollections.find(
      (c) => c.id === params.collectionId,
    );
    if (!collection) {
      return undefined;
    }

    // グループを取得
    const group = collection.attributeGroupSettings.find(
      (g) => g.id === params.groupId,
    );
    if (!group) {
      return undefined;
    }

    // 属性を取得
    const attribute = group.attributes.find((a) => a.id === params.attributeId);
    if (attribute) {
      return {
        attribute,
        collection,
        group,
        params,
      };
    } else {
      return undefined;
    }
  } catch (_error) {
    return undefined;
  }
}
